import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { showBasicAlert } from "../../core/common/useAlert";
import Loader from "../../core/common/loader";
import ImageWithBasePath from "../common/imageWithBasePath";

interface BulkDocumentRule {
  rule_type: string;
  target_value: any;
  access_type: string;
}

interface BulkDocument {
  primary_id: number;
  doc_id: number;
  name: string;
  document_type: string;
  original_document_type: string;
  size: number;
  created_at: string;
  updated_at: string;
  owner_id: number;
  owner_info?: {
    id: number;
    name: string;
    email: string;
    profile_pic?: string;
  };
  mime_type: string;
  path: string;
  shared_count: number;
  is_bulk_upload: boolean;
  rules?: BulkDocumentRule[];
  permissions_summary?: {
    total_shared: number;
    can_view: number;
    can_edit: number;
    sharing_mode: 'rule_based' | 'user_specific';
  };
}

interface FilterState {
  companyType: string | null;
  companyId: number | null;
  department: string | null;
  departmentId: string | null; // Add this
  designation: string | null;
  designationId: string | null; // Add this
}

interface BulkDocumentsModalProps {
  modalId: string;
  signature: string | null;
  companyId?: number | null;
  companyType?: string | null;
  allEmployeesData?: any[];
  allCompanies?: any[];
  allDepartments?: any[];
  allDesignations?: any[];
}

const BulkDocumentsModal: React.FC<BulkDocumentsModalProps> = ({
  modalId,
  signature,
  companyId,
  companyType,
  allEmployeesData = [],
  allCompanies = [],
  allDepartments = [],
  allDesignations = [],
}) => {
  const [filters, setFilters] = useState<FilterState>({
    companyType: null,
    companyId: null,
    department: null,
    departmentId: null,
    designation: "all",
    designationId: null,
  });
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [bulkDocuments, setBulkDocuments] = useState<BulkDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<BulkDocument | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [showRuleManager, setShowRuleManager] = useState(false);
  const [currentDocumentRules, setCurrentDocumentRules] = useState<BulkDocumentRule[]>([]);
  const [selectedRuleDocument, setSelectedRuleDocument] = useState<BulkDocument | null>(null);
  const [newRuleType, setNewRuleType] = useState('all_company_users');
  const [newRuleTarget, setNewRuleTarget] = useState<any>(null);
  const [managingRules, setManagingRules] = useState(false);

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Load documents when modal opens
      modalElement.addEventListener("show.bs.modal", () => {
        fetchBulkDocuments();
      });

      // Reset state when modal closes
      modalElement.addEventListener("hidden.bs.modal", () => {
        setBulkDocuments([]);
        setSelectedDocument(null);
        setShowPreview(false);
        setShowRuleManager(false);
        setSelectedRuleDocument(null);
      });
    }
  }, [modalId]);

  useEffect(() => {
    if (showRuleManager && selectedRuleDocument && companyId && companyType) {
      setFilters({
        companyId: companyId,
        companyType: companyType,
        department: null,
        designation: null,
        departmentId: null,
        designationId: null
      });

      const company = allCompanies.find(
        (c: any) => c.id === companyId && c.type === companyType
      );
    }
  }, [showRuleManager, selectedRuleDocument, companyId, companyType, allCompanies]);

  const fetchBulkDocuments = async () => {
    if (!signature) {
      showBasicAlert("Authentication required");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("driveService/getBulkUploadDocuments", {
        signature,
        company_id: companyId || undefined,
        company_type: companyType || undefined,
      });

      if (response.data?.message?.[0]?.status) {
        const data = response.data.message[0].data;
        setBulkDocuments(data.documents || []);
      } else {
        showBasicAlert(
          response.data?.message?.[0]?.msg || "Failed to fetch bulk documents"
        );
      }
    } catch (error: any) {
      console.error("Error fetching bulk documents:", error);
      showBasicAlert(
        error?.data?.message?.[0]?.msg ||
        "Error fetching bulk documents"
      );
    } finally {
      setLoading(false);
    }
  };

  const getCompanyOptions = () => {
    if (!allCompanies || allCompanies.length === 0) return [];

    return allCompanies.map((company: any) => ({
      value: company.id + "-" + company.type,
      label: company.name + " - " + company.type,
    }));
  };

  const getDepartmentOptions = () => {
    if (!filters.companyId || !allEmployeesData || allEmployeesData.length === 0) {
      return [
        { value: "all", label: "All Departments", id: null },
        ...Array.from(new Set(allEmployeesData.map((emp: any) => emp.Department)))
          .filter((dept) => dept)
          .sort()
          .map((dept) => ({
            value: dept,
            label: dept,
            id: allDepartments?.find((d: any) => d.Department === dept)?.key || null
          })),
      ];
    }

    const filteredEmployees = allEmployeesData.filter(
      (emp: any) =>
        emp.CompanyId === filters.companyId &&
        emp.CompanyType === filters.companyType
    );

    const departments = Array.from(
      new Set(filteredEmployees.map((emp: any) => emp.Department))
    )
      .filter((dept) => dept)
      .sort();

    return [
      { value: "all", label: "All Departments", id: null },
      ...departments.map((dept) => ({
        value: dept,
        label: dept,
        id: allDepartments?.find((d: any) => d.Department === dept)?.key || null
      })),
    ];
  };

  const getDesignationOptions = () => {
    if (
      (!filters.companyId && !filters.department) ||
      !allEmployeesData ||
      allEmployeesData.length === 0
    ) {
      return [
        { value: "all", label: "All Designations", id: null },
        ...Array.from(new Set(allEmployeesData.map((emp: any) => emp.Designation)))
          .filter((desig) => desig)
          .sort()
          .map((desig) => ({
            value: desig,
            label: desig,
            id: allDesignations?.find((d: any) => d.Designation === desig)?.key || null
          })),
      ];
    }

    // Filter employees by selected filters
    let filteredEmployees = allEmployeesData;

    if (filters.companyId && filters.companyType) {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) =>
          emp.CompanyId === filters.companyId &&
          emp.CompanyType === filters.companyType
      );
    }

    if (filters.department && filters.department !== "all") {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Department === filters.department
      );
    }

    const designations = Array.from(
      new Set(filteredEmployees.map((emp: any) => emp.Designation))
    )
      .filter((desig) => desig)
      .sort();

    return [
      { value: "all", label: "All Designations", id: null },
      ...designations.map((desig) => ({
        value: desig,
        label: desig,
        id: allDesignations?.find((d: any) => d.Designation === desig)?.key || null
      })),
    ];
  };


  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    const selectedOption = e.target.options[e.target.selectedIndex];
    const departmentId = selectedOption.getAttribute('data-id');

    if (value != "all") {
      setNewRuleType("by_designation");
    }

    setFilters(prev => ({
      ...prev,
      department: value === "all" ? null : value,
      departmentId: value === "all" ? null : departmentId,
      designation: "all",
      designationId: null,
    }));
    setFiltersApplied(false);
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const selectedOption = e.target.options[e.target.selectedIndex];
    const designationId = selectedOption.getAttribute('data-id');


    setNewRuleType("by_designation");
    setFilters(prev => ({
      ...prev,
      designation: value,
      designationId,
    }));

    if (value === "all") {
      setFilters(prev => ({
        ...prev,
        designationId: null,
      }));
    }
    setFiltersApplied(false);
  };

  const applyFiltersAndLog = () => {

    if (!allEmployeesData || allEmployeesData.length === 0) return [];

    let filteredEmployees = [...allEmployeesData];
    if (filters.department == null) {
      setNewRuleType("all_company_users");
    }

    if (filters.companyId && filters.companyType) {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) =>
          emp.CompanyId === filters.companyId &&
          emp.CompanyType === filters.companyType
      );
    }

    if (filters.department) {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Department === filters.department
      );
    }

    if (filters.designation && filters?.designation !== "all") {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Designation === filters.designation
      );
    }

    const hasActiveFilters = !!filters.companyId && !!filters.companyType;

    setFiltersApplied(hasActiveFilters && filteredEmployees.length > 0);

    return filteredEmployees;
  };


  const handleUpdateBulkDocumentRule = async (documentId: number, ruleType: string, targetValue: any = null) => {
    try {
      const response = await api.post("driveService/updateBulkDocumentRule", {
        signature,
        document_id: documentId,
        rule_type: ruleType,
        target_value: targetValue,
        access_type: "view"
      });

      if (response.data?.message?.[0]?.status) {
        showBasicAlert("Document access rule updated successfully!");
        return true;
      } else {
        showBasicAlert(response.data?.message?.[0]?.msg || "Failed to update rule");
        return false;
      }
    } catch (error: any) {
      console.error("Error updating rule:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg || "Failed to update rule"
      );
      return false;
    }
  };

  const handleManageRules = (document: BulkDocument) => {
    setSelectedRuleDocument(document);
    setCurrentDocumentRules(document.rules || []);
    setNewRuleType('all_company_users');
    setNewRuleTarget(null);
    setShowRuleManager(true);
  };

  const handleAddRule = async () => {
    if (!selectedRuleDocument) return;

    setManagingRules(true);

    let targetValue = null;
    let updatedRuleType = newRuleType;


    // Set target value based on rule type and filters
    switch (newRuleType) {
      case 'by_department':

        if (filters.department && filters.department !== "all" && filters.departmentId) {
          targetValue = { department_ids: [filters.departmentId] };
        }
        break;
      case 'by_designation':
        if (filters.designation && filters?.designation == "all") {
          updatedRuleType = "by_department"
          targetValue = { department_ids: [filters.departmentId] };
        } else if (filters.designation && filters.designationId) {
          targetValue = { designation_ids: [filters.designationId] };
        }
        break;
      default:
        targetValue = null;
    }

    const success = await handleUpdateBulkDocumentRule(
      selectedRuleDocument.doc_id,
      updatedRuleType,
      targetValue
    );

    if (success) {
      await fetchBulkDocuments();
      setShowRuleManager(false);
    }

    setManagingRules(false);
  };

  const handleRemoveRule = async (ruleType: string) => {
    if (!selectedRuleDocument) return;

    const success = await handleUpdateBulkDocumentRule(
      selectedRuleDocument.doc_id,
      'all_company_users',
      null
    );

    if (success) {
      await fetchBulkDocuments();
    }
  };

  const handleViewDocument = async (document: BulkDocument) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  const handleDownload = async (doc: BulkDocument) => {
    try {
      const response = await api.post(
        "driveService/downloadDriveItem",
        {
          signature,
          id: doc.doc_id,
        },
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.name);

      window.document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      showBasicAlert("Failed to download document");
    }
  };

  const handleDeleteDocument = async (document: BulkDocument) => {
    if (!signature) {
      showBasicAlert("Authentication required");
      return;
    }

    try {
      const response = await api.post("driveService/deleteBulkDocument", {
        signature,
        document_id: document.doc_id,
      });

      if (response.data?.message?.[0]?.status) {
        showBasicAlert("Bulk document deleted successfully");
        setBulkDocuments((prev) =>
          prev.filter((doc) => doc.doc_id !== document.doc_id)
        );

        if (selectedDocument && selectedDocument.doc_id === document.doc_id) {
          setShowPreview(false);
          setSelectedDocument(null);
        }
      } else {
        showBasicAlert(
          response.data?.message?.[0]?.msg || "Failed to delete document"
        );
      }
    } catch (error: any) {
      console.error("Error deleting document:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg || "Error deleting document"
      );
    }
  };

  const handleCopyLink = async (document: BulkDocument) => {
    try {
      const response = await api.post("driveService/getFileLink", {
        signature,
        file_id: document.doc_id,
      });

      if (
        response.data?.message?.[0]?.status &&
        response.data?.message?.[0]?.data?.link
      ) {
        const fileLink = response.data.message[0].data.link;
        await navigator.clipboard.writeText(fileLink);
        showBasicAlert("Link copied to clipboard!");
      } else {
        showBasicAlert("Failed to get file link");
      }
    } catch (error) {
      console.error("Error copying link:", error);
      showBasicAlert("Failed to copy link");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileIcon = (mimeType: string, fileName: string) => {
    if (mimeType?.includes("pdf")) return "pdf-icon.svg";
    if (mimeType?.includes("image")) return "image.svg";
    if (mimeType?.includes("word")) return "file.svg";
    if (mimeType?.includes("excel")) return "xls-icon.svg";
    if (mimeType?.includes("presentation")) return "ppt-icon.svg";
    return "file.svg";
  };

  const getRuleDisplayName = (ruleType: string): string => {
    const ruleNames: { [key: string]: string } = {
      'all_company_users': 'All Departments',
      'by_role': 'By Role',
      'by_department': 'By Department',
      'by_designation': 'By Designation',
      'custom_users': 'Specific Users'
    };
    return ruleNames[ruleType] || ruleType;
  };

  const handleSort = (column: string) => {
    const isSameColumn = sortColumn === column;
    const newDirection =
      isSameColumn && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const sortedDocuments = [...bulkDocuments].sort((a, b) => {
    let comparison = 0;

    if (sortColumn === "name") {
      comparison = (a.name || "").localeCompare(b.name || "");
    } else if (sortColumn === "document_type") {
      comparison = (a.original_document_type || "").localeCompare(
        b.original_document_type || ""
      );
    } else if (sortColumn === "size") {
      comparison = (a.size || 0) - (b.size || 0);
    } else if (sortColumn === "shared_count") {
      comparison = (a.shared_count || 0) - (b.shared_count || 0);
    } else if (sortColumn === "created_at") {
      comparison =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  const RuleManagerModal = () => {
    if (!showRuleManager || !selectedRuleDocument) return null;

    // Find the company name for display
    const selectedCompany = allCompanies.find(
      (c: any) => c.id === companyId && c.type === companyType
    );

    return (
      <div className="modal-backdrop" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ti ti-settings me-2"></i>
                  Manage Access Rules: {selectedRuleDocument.original_document_type}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRuleManager(false)}
                  disabled={managingRules}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <h6>Current Rules:</h6>
                  {currentDocumentRules.length > 0 ? (
                    <div className="list-group">
                      {currentDocumentRules.map((rule, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{getRuleDisplayName(rule.rule_type)}</strong>
                            {rule.target_value && (
                              <small className="text-muted d-block">
                                {JSON.stringify(rule.target_value)}
                              </small>
                            )}
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveRule(rule.rule_type)}
                            disabled={managingRules}
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No rules defined. Document uses default permissions.</p>
                  )}
                </div>

                <div className="card mb-4">
                  <div className="card-header d-flex align-items-center">
                    <h6 className="mb-0">Define Access Rule</h6>
                    {filtersApplied && (
                      <span className="badge bg-success ms-2">
                        Filters Applied
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Auto-filled Company Display (Read-only) */}
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Company</label>
                        <div className="form-control bg-light">
                          {selectedCompany ? (
                            <div className="d-flex justify-content-between align-items-center">
                              <span>
                                {selectedCompany.name} - {selectedCompany.type}
                              </span>
                              <i className="ti ti-lock text-muted" title="Company is pre-selected based on document"></i>
                            </div>
                          ) : (
                            <span className="text-muted">No company selected</span>
                          )}
                        </div>
                        <small className="text-muted">Company is pre-selected based on the document</small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Rule Type</label>
                        <select
                          className="form-control"
                          value={newRuleType}
                          onChange={(e) => setNewRuleType(e.target.value)}
                        >
                          <option value="">Select Rule</option>
                          <option value="by_department">By Department</option>
                          <option value="by_designation">By Designation</option>
                        </select>
                      </div>

                      {(newRuleType === 'by_department' || newRuleType === 'by_designation' || newRuleType === 'all_company_users') && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Department</label>
                          <select
                            className="form-control"
                            value={filters.department || "all"}
                            onChange={handleDepartmentChange}
                            disabled={!companyId}
                          >
                            {companyId && getDepartmentOptions().map((option, index) => (
                              <option
                                key={index}
                                value={option.value}
                                data-id={option.id}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {!companyId && (
                            <small className="text-danger">Please select a company first</small>
                          )}
                        </div>
                      )}

                      {newRuleType === 'by_designation' && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Designation</label>
                          <select
                            className="form-control"
                            value={filters.designation || "all"}
                            onChange={handleDesignationChange}
                            disabled={!filters.department}
                          >
                            {filters.department && getDesignationOptions().map((option, index) => (
                              <option
                                key={index}
                                value={option.value}
                                data-id={option.id}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(newRuleType === 'by_department' || newRuleType === 'by_designation') && (
                        <div className="col-md-12 mb-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary w-100"
                            onClick={applyFiltersAndLog}
                            disabled={!companyId}
                          >
                            {filtersApplied ? "Re-apply Filters" : "Apply Filters"}
                          </button>
                          {!companyId && (
                            <small className="text-danger d-block mt-1">Company selection is required to apply filters</small>
                          )}
                        </div>
                      )}

                      <div className="col-md-12">
                        <small className="text-muted">
                          {newRuleType === 'all_company_users' && `All users in ${selectedCompany?.name || 'the company'} will have access`}
                          {newRuleType === 'by_department' && `Users in selected departments of ${selectedCompany?.name || 'the company'} will have access`}
                          {newRuleType === 'by_designation' && `Users with selected designations in ${selectedCompany?.name || 'the company'} will have access`}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRuleManager(false)}
                  disabled={managingRules}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddRule}
                  disabled={managingRules || !filtersApplied}
                >Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showRuleManager && <RuleManagerModal />}

      <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="ti ti-files me-2"></i>
                Bulk Upload Documents
              </h5>
              <div className="d-flex align-items-center">
                <div className="btn-group me-2" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "grid" ? "btn-primary" : "btn-outline-primary"
                      }`}
                    onClick={() => setViewMode("grid")}
                    title="Grid View"
                  >
                    <i className="ti ti-layout-grid"></i>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"
                      }`}
                    onClick={() => setViewMode("list")}
                    title="List View"
                  >
                    <i className="ti ti-list"></i>
                  </button>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="text-center py-5">
                  <Loader />
                  <p className="mt-3">Loading bulk documents...</p>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0">
                        {bulkDocuments.length} Bulk Document
                        {bulkDocuments.length !== 1 ? "s" : ""} Found
                      </h6>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={fetchBulkDocuments}
                      >
                        <i className="ti ti-refresh me-1"></i>
                        Refresh
                      </button>
                    </div>
                  </div>

                  {bulkDocuments.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="avatar avatar-lg bg-light rounded-circle mb-3">
                        <i className="ti ti-folder-off fs-24 text-muted"></i>
                      </div>
                      <h5>No Bulk Documents Found</h5>
                      <p className="text-muted">
                        No bulk upload documents found for this company.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary mt-2"
                        onClick={fetchBulkDocuments}
                      >
                        <i className="ti ti-refresh me-1"></i>
                        Check Again
                      </button>
                    </div>
                  ) : viewMode === "grid" ? (
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                      {sortedDocuments.map((doc) => (
                        <div className="col" key={doc.doc_id}>
                          <div className="card h-100 border">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-center">
                                  <ImageWithBasePath
                                    src={`assets/img/icons/${getFileIcon(
                                      doc.mime_type,
                                      doc.name
                                    )}`}
                                    alt={doc.name}
                                    width={32}
                                    height={32}
                                    className="me-2"
                                  />
                                  <div>
                                    <h6
                                      className="mb-0 text-truncate"
                                      title={doc.original_document_type}
                                    >
                                      {doc.original_document_type}
                                    </h6>
                                    <small className="text-muted">
                                      <span className="badge bg-info me-1">
                                        BULK
                                      </span>

                                    </small>
                                  </div>
                                </div>
                                <div className="dropdown">
                                  <button
                                    className="btn btn-sm btn-light"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="ti ti-dots-vertical"></i>
                                  </button>
                                  <ul className="dropdown-menu dropdown-menu-end">
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleDownload(doc)}
                                      >
                                        <i className="ti ti-download me-2"></i>
                                        Download
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleCopyLink(doc)}
                                      >
                                        <i className="ti ti-link me-2"></i>
                                        Copy Link
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleManageRules(doc)}
                                      >
                                        <i className="ti ti-settings me-2"></i>
                                        Manage Rules
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleViewDocument(doc)}
                                      >
                                        <i className="ti ti-eye me-2"></i>
                                        View Details
                                      </button>
                                    </li>
                                    <li>
                                      <hr className="dropdown-divider" />
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => handleDeleteDocument(doc)}
                                      >
                                        <i className="ti ti-trash me-2"></i>
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted d-block mb-1">
                                  File:
                                </small>
                                <p
                                  className="mb-0 text-truncate"
                                  title={doc.name}
                                >
                                  <i className="ti ti-file me-1"></i>
                                  {doc.name}
                                </p>
                              </div>

                              <div className="row g-2 mb-3">
                                <div className="col-6">
                                  <small className="text-muted d-block mb-1">
                                    Size:
                                  </small>
                                  <span className="badge bg-light text-dark">
                                    {formatFileSize(doc.size)}
                                  </span>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block mb-1">
                                    Uploaded:
                                  </small>
                                  <span className="badge bg-light text-dark">
                                    {formatDate(doc.created_at)}
                                  </span>
                                </div>
                              </div>

                              {doc.rules && doc.rules.length > 0 && (
                                <div className="mb-3">
                                  <small className="text-muted d-block mb-1">
                                    Access Rules:
                                  </small>
                                  <div className="d-flex flex-wrap gap-1">
                                    {doc.rules.map((rule, idx) => (
                                      <span key={idx} className="badge bg-success">
                                        {getRuleDisplayName(rule.rule_type)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {doc.owner_info && (
                                <div className="d-flex align-items-center">
                                  <small className="text-muted me-2">
                                    Uploaded by:
                                  </small>
                                  <div className="d-flex align-items-center">
                                    {doc.owner_info.profile_pic ? (
                                      <img
                                        src={doc.owner_info.profile_pic}
                                        alt={doc.owner_info.name}
                                        className="rounded-circle me-1"
                                        width={20}
                                        height={20}
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-1"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          fontSize: "10px",
                                        }}
                                      >
                                        {doc.owner_info.name.charAt(0)}
                                      </div>
                                    )}
                                    <small>{doc.owner_info.name}</small>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th
                              onClick={() => handleSort("document_type")}
                              style={{ cursor: "pointer" }}
                            >
                              Document Type
                              {sortColumn === "document_type" && (
                                <i
                                  className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                                    } ms-1`}
                                ></i>
                              )}
                            </th>
                            <th
                              onClick={() => handleSort("name")}
                              style={{ cursor: "pointer" }}
                            >
                              File Name
                              {sortColumn === "name" && (
                                <i
                                  className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                                    } ms-1`}
                                ></i>
                              )}
                            </th>
                            <th>Access Type</th>
                            <th
                              onClick={() => handleSort("size")}
                              style={{ cursor: "pointer" }}
                            >
                              Size
                              {sortColumn === "size" && (
                                <i
                                  className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                                    } ms-1`}
                                ></i>
                              )}
                            </th>
                            <th
                              onClick={() => handleSort("created_at")}
                              style={{ cursor: "pointer" }}
                            >
                              Uploaded
                              {sortColumn === "created_at" && (
                                <i
                                  className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                                    } ms-1`}
                                ></i>
                              )}
                            </th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedDocuments.map((doc) => (
                            <tr key={doc.doc_id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-info me-2">BULK</span>
                                  <span
                                    className="text-truncate"
                                    style={{ maxWidth: "200px" }}
                                    title={doc.original_document_type}
                                  >
                                    {doc.original_document_type}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <ImageWithBasePath
                                    src={`assets/img/icons/${getFileIcon(
                                      doc.mime_type,
                                      doc.name
                                    )}`}
                                    alt={doc.name}
                                    width={24}
                                    height={24}
                                    className="me-2"
                                  />
                                  <span
                                    className="text-truncate"
                                    style={{ maxWidth: "250px" }}
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {doc.rules && doc.rules.length > 0 ? (
                                  <div className="d-flex flex-wrap gap-1">
                                    {doc.rules.map((rule, idx) => (
                                      <span key={idx} className="badge bg-success" title={JSON.stringify(rule.target_value || {})}>
                                        {getRuleDisplayName(rule.rule_type)}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="badge bg-secondary">No Access</span>
                                )}
                              </td>
                              <td>{formatFileSize(doc.size)}</td>
                              <td>{formatDate(doc.created_at)}</td>
                              <td>
                                <div
                                  className="btn-group btn-group-sm"
                                  role="group"
                                >
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleDownload(doc)}
                                    title="Download"
                                  >
                                    <i className="ti ti-download"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleCopyLink(doc)}
                                    title="Copy Link"
                                  >
                                    <i className="ti ti-link"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-success"
                                    onClick={() => handleManageRules(doc)}
                                    title="Manage Rules"
                                  >
                                    <i className="ti ti-settings"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-info"
                                    onClick={() => handleViewDocument(doc)}
                                    title="View Details"
                                  >
                                    <i className="ti ti-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteDocument(doc)}
                                    title="Delete Document"
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {showPreview && selectedDocument && (
              <div className="modal-footer border-top">
                <div className="w-100">
                  <h6>Document Preview</h6>
                  <div className="row">
                    <div className="col-md-4">
                      <strong>File Name:</strong>
                      <p>{selectedDocument.name}</p>
                    </div>
                    <div className="col-md-4">
                      <strong>Document Type:</strong>
                      <p>{selectedDocument.original_document_type}</p>
                    </div>
                    {selectedDocument.rules && selectedDocument.rules.length > 0 && (
                      <div className="col-md-4">
                        <strong>Access Rules:</strong>
                        {selectedDocument.rules.map((rule, idx) => (
                          <div key={idx} className="mb-1">
                            <span className="badge bg-success me-2">
                              {getRuleDisplayName(rule.rule_type)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setShowPreview(false)}
                    >
                      Close Preview
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkDocumentsModal;